import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField } from '@mui/material';
import NewModal from '../../../../Shared/NewModal';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as UnassignSuccessIcon } from '../../../../../images/icons/unassign_success.svg';
import { ReactComponent as UnassignFailedIcon } from '../../../../../images/icons/unassign_failed.svg';
import { dimGrey } from '../../../../../mainTheme';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import {
  clearDeviceActionStatus,
  unassignDeviceToOwner,
} from '../../../../../store/devices/actionCreators';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface UnassignDialogProps {
  onClose: () => void;
  deviceId: number;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

export const UnassignDialog: React.FC<UnassignDialogProps> = ({
  onClose,
  deviceId,
}) => {
  const { actionStatus } = useSelector<
    RootState,
    { actionStatus: DevicesStore['actionStatus'] }
  >((state) => ({
    actionStatus: state.devices.actionStatus,
  }));

  const dispatch = useDispatch();
  const [step, setStep] = useState<'confirm' | 'success' | 'failed'>('confirm');
  const [verificationText, setVerificationText] = useState('');

  const handleVerification = () => {
    if (verificationText.toLowerCase() === 'unassign') {
      dispatch(unassignDeviceToOwner(deviceId));
    }
  };

  useEffect(() => {
    if (actionStatus.type === 'unassign_owner') {
      if (actionStatus.success) {
        setStep('success');
      } else if (actionStatus.success === false) {
        setStep('failed');
      }
    }
  }, [actionStatus]);

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('confirm');
    setVerificationText('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderConfirmView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Unassign Hub</Typography>
      <Typography sx={typography.subText}>
        This will delete the database and unassign the hub from the current
        owner. Once complete, the Hub will reboot.
      </Typography>
      <Typography sx={{ ...typography.subText, fontWeight: 700, ml: 2 }}>
        Type the word &quot;unassign&quot; to confirm.
      </Typography>
      <TextField
        fullWidth
        value={verificationText}
        onChange={(e) => setVerificationText(e.target.value)}
        placeholder="Type 'unassign'"
        variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return renderConfirmView();
      case 'success':
        return (
          <StatusView
            icon={<UnassignSuccessIcon />}
            title="Hub has been unassigned"
            message="The Hub has been successfully unassigned. You can reassign it at any time."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<UnassignFailedIcon />}
            title="Failed to unassign Hub"
            message="Please try again at a later time."
          />
        );
    }
  };

  const actionConfig = {
    success: { text: 'OK', onClick: handleClose },
    failed: { text: 'Try again', onClick: handleReset },
    confirm: { text: 'Unassign', onClick: handleVerification },
  };

  return (
    <NewModal
      showCloseX
      title="Unassign"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: {
          ...actionConfig[step],
          disabled:
            step === 'confirm' && verificationText.toLowerCase() !== 'unassign',
        },
        secondaryAction:
          step === 'confirm'
            ? {
                text: 'Back',
                onClick: handleClose,
              }
            : undefined,
      }}
    >
      {renderContent()}
    </NewModal>
  );
};
